import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Plus, BookMarked, Filter, Search, X, Radio } from 'lucide-react';
import { cn, MathText, Paginator, paginate } from '@jeraisonne/ui';
import { SUBJECTS, SCHOOL_LEVELS } from '@jeraisonne/db';
import type { SubjectSlug, SchoolLevel } from '@jeraisonne/db';
import { useAuth, isAdmin } from '@/lib/auth';
import {
  useMyExercises,
  useSearchExercises,
  useLiveSchoolExercises,
  EXERCISE_SEARCH_MIN_LEN,
} from '@/lib/tutor/queries';
import type {
  MyExerciseSummary,
  LiveSchoolExercise,
  LiveChallengeStatus,
} from '@/lib/tutor/queries';
import { TutorLibraryTabs } from '@/components/tutor-library-tabs';
import { AvatarCircle } from '@/components/avatar-circle';

/** Filtres client-side, partagés par la vue « Mes exercices » et la recherche. */
export type ExerciseFilters = {
  level: SchoolLevel | '';
  subject: SubjectSlug | '';
  difficulty: 'easy' | 'medium' | 'hard' | '';
  kind: '' | 'classic' | 'fundamental' | 'diagnostic';
};

/** Pur — un exercice satisfait-il les filtres actifs (matière/niveau/difficulté/type) ? */
export function matchesExerciseFilters(e: MyExerciseSummary, f: ExerciseFilters): boolean {
  const levelMatch = !f.level || e.level === f.level;
  const subjectMatch = !f.subject || e.subject === f.subject;
  const diffMatch = !f.difficulty || e.difficulty === f.difficulty;
  const kindMatch =
    !f.kind ||
    (f.kind === 'fundamental'
      ? e.is_fundamental
      : f.kind === 'diagnostic'
        ? e.is_diagnostic
        : !e.is_fundamental && !e.is_diagnostic);
  return levelMatch && subjectMatch && diffMatch && kindMatch;
}

const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));
const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

export const DIFFICULTY_META = {
  easy: { label: 'Facile', cls: 'bg-xp-50    text-xp-600   border-xp-100' },
  medium: { label: 'Moyen', cls: 'bg-level-50 text-level-600 border-level-100' },
  hard: { label: 'Difficile', cls: 'bg-accent-50 text-accent-600 border-accent-100' },
} as const;

/**
 * Méta d'affichage par statut éditorial. Doit couvrir TOUTES les valeurs de
 * l'enum DB `content_status` (cf. migration 0087 qui a ajouté `awaiting_review`).
 * Une valeur manquante ferait planter `ExerciseCard` sur `stat.cls`.
 */
export const STATUS_META = {
  draft: { label: 'Brouillon', cls: 'bg-ink-100 text-ink-600' },
  published: { label: 'Publié', cls: 'bg-xp-50  text-xp-600' },
  archived: { label: 'Archivé', cls: 'bg-ink-50  text-ink-400' },
  awaiting_review: { label: 'En relecture', cls: 'bg-accent-50 text-accent-600' },
} as const;

/** Méta d'affichage par statut de défi Live School. */
export const LIVE_STATUS_META: Record<LiveChallengeStatus, { label: string; cls: string }> = {
  scheduled: { label: 'Programmé', cls: 'bg-level-50 text-level-700' },
  live: { label: 'En direct', cls: 'bg-error-50 text-error-600' },
  finished: { label: 'Terminé', cls: 'bg-ink-100 text-ink-500' },
  snapshotted: { label: 'Clôturé', cls: 'bg-ink-100 text-ink-500' },
};

/** Date + heure de diffusion d'un défi (fr-FR, heure locale du navigateur). */
const liveDateFmt = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Pur — répartit les exercices Live School en « à venir » / « déjà diffusés »
 * après application des filtres (niveau du DÉFI, matière/difficulté/type de
 * l'exercice, recherche par titre). Chaque groupe est trié : à venir = le plus
 * proche d'abord, passés = le plus récent d'abord.
 */
export function partitionLiveSchool(
  items: LiveSchoolExercise[],
  opts: { now: number; filterLevel: SchoolLevel | ''; filters: ExerciseFilters; search: string },
): { upcoming: LiveSchoolExercise[]; past: LiveSchoolExercise[] } {
  const q = opts.search.trim().toLowerCase();
  const filtered = items.filter((item) => {
    if (opts.filterLevel && item.live.challenge_level !== opts.filterLevel) return false;
    // Le niveau est filtré sur le DÉFI ci-dessus → on le neutralise ici.
    if (!matchesExerciseFilters(item, { ...opts.filters, level: '' })) return false;
    if (q && !item.title.toLowerCase().includes(q)) return false;
    return true;
  });
  const upcoming = filtered
    .filter((i) => Date.parse(i.live.starts_at) >= opts.now)
    .sort((a, b) => Date.parse(a.live.starts_at) - Date.parse(b.live.starts_at));
  const past = filtered
    .filter((i) => Date.parse(i.live.starts_at) < opts.now)
    .sort((a, b) => Date.parse(b.live.starts_at) - Date.parse(a.live.starts_at));
  return { upcoming, past };
}

type ChapterGroup = {
  id: string | null;
  title: string;
  level: SchoolLevel | null;
  exercises: MyExerciseSummary[];
};
type SubjectGroup = { label: string; chapters: ChapterGroup[] };

export function TutorExercisesPage() {
  const { user, profile } = useAuth();
  const tutorId = user?.id ?? '';
  const admin = isAdmin(profile, user?.email);
  const { data: exercises, isLoading } = useMyExercises(tutorId);

  const [filterLevel, setFilterLevel] = useState<SchoolLevel | ''>('');
  const [filterSubject, setFilterSubject] = useState<SubjectSlug | ''>('');
  const [filterDifficulty, setFilterDifficulty] = useState<'easy' | 'medium' | 'hard' | ''>('');
  const [filterKind, setFilterKind] = useState<'' | 'classic' | 'fundamental' | 'diagnostic'>('');
  const [showFilters, setShowFilters] = useState(false);

  // Mode « Live School » (admin) : liste les exercices rattachés à un défi live,
  // chaque carte affichant la date/heure de diffusion. Réservé à l'admin (RLS).
  const [liveMode, setLiveMode] = useState(false);
  const liveEnabled = liveMode && admin;
  const { data: liveExercises, isLoading: liveLoading } = useLiveSchoolExercises(liveEnabled);

  // Recherche (tous auteurs confondus) débouncée à 300 ms.
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  const searching = !liveMode && q.length >= EXERCISE_SEARCH_MIN_LEN;
  // En mode Live School la recherche filtre localement (par titre) → pas d'appel global.
  const { data: searchResults, isLoading: searchLoading } = useSearchExercises(liveMode ? '' : q);

  const filters: ExerciseFilters = {
    level: filterLevel,
    subject: filterSubject,
    difficulty: filterDifficulty,
    kind: filterKind,
  };

  // Levels present in exercises (for pills). En mode Live School, on compte les
  // NIVEAUX DES DÉFIS pour que les pastilles reflètent la liste affichée.
  const levelOptions = useMemo(() => {
    const counts = new Map<SchoolLevel, number>();
    if (liveMode) {
      for (const item of liveExercises ?? []) {
        const lvl = item.live.challenge_level;
        counts.set(lvl, (counts.get(lvl) ?? 0) + 1);
      }
    } else {
      for (const ex of exercises ?? []) {
        if (ex.level) counts.set(ex.level, (counts.get(ex.level) ?? 0) + 1);
      }
    }
    return SCHOOL_LEVELS.filter((l) => counts.has(l.value)).map((l) => ({
      value: l.value,
      label: l.label,
      count: counts.get(l.value) ?? 0,
    }));
  }, [exercises, liveExercises, liveMode]);

  // Répartition des exercices Live School (à venir / passés) après filtres.
  const live = useMemo(
    () =>
      partitionLiveSchool(liveExercises ?? [], {
        now: Date.now(),
        filterLevel,
        filters,
        search: searchInput,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveExercises, filterLevel, filterSubject, filterDifficulty, filterKind, searchInput],
  );

  const displayed = useMemo(
    () => (exercises ?? []).filter((e) => matchesExerciseFilters(e, filters)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exercises, filterLevel, filterSubject, filterDifficulty, filterKind],
  );

  // Résultats de recherche (tous auteurs) après application des mêmes filtres.
  const searchDisplayed = useMemo(
    () => (searchResults ?? []).filter((e) => matchesExerciseFilters(e, filters)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResults, filterLevel, filterSubject, filterDifficulty, filterKind],
  );

  // Group by subject → chapter, puis pagination (20 exercices par page) dans
  // l'ordre de regroupement : une page peut couvrir plusieurs chapitres.
  const fullGroups = useMemo(() => groupExercises(displayed), [displayed]);
  const chapterTotals = useMemo(() => {
    const m = new Map<string, number>();
    for (const [, sg] of fullGroups)
      for (const c of sg.chapters) m.set(c.id ?? c.title, c.exercises.length);
    return m;
  }, [fullGroups]);
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [filterLevel, filterSubject, filterDifficulty, filterKind, q]);
  const paged = useMemo(
    () =>
      paginate(
        fullGroups.flatMap(([, sg]) => sg.chapters.flatMap((c) => c.exercises)),
        page,
      ),
    [fullGroups, page],
  );
  const grouped = useMemo(() => groupExercises(paged.items), [paged.items]);
  const changePage = (p: number) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveFilter = !!filterSubject || !!filterDifficulty || !!filterKind;

  return (
    <div className="mx-auto max-w-4xl p-6 md:p-8">
      <TutorLibraryTabs />
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight md:text-[26px]">
            Mes exercices
          </h1>
          <p className="text-ink-500 mt-1 font-sans text-sm">
            {exercises
              ? `${exercises.length} exercice${exercises.length !== 1 ? 's' : ''}`
              : 'Exercices créés et publiés.'}
          </p>
        </div>
        <Link
          to="/tutor/exercises/new"
          className="bg-accent-400 font-display hover:bg-accent-500 inline-flex h-11 shrink-0 items-center gap-2 rounded-xl px-5 text-sm font-bold text-white shadow-md transition-all active:scale-[.98]"
        >
          <Plus className="h-4 w-4" />
          Créer un exercice
        </Link>
      </div>

      {/* Recherche — tous auteurs confondus, sur le titre / l'énoncé / le contenu */}
      <div className="relative mb-4">
        <Search className="text-ink-400 pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2" />
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Rechercher un exercice (titre, énoncé, contenu…)"
          aria-label="Rechercher un exercice"
          className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:border-primary-500 h-11 w-full rounded-xl border bg-white pl-10 pr-10 font-sans text-sm focus:outline-none"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput('')}
            aria-label="Effacer la recherche"
            className="text-ink-400 hover:text-ink-700 absolute right-2.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Level pills */}
      {levelOptions.length > 0 && (
        <div className="-mx-6 mb-4 flex gap-2 overflow-x-auto px-6 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button
            onClick={() => setFilterLevel('')}
            className={cn(
              'font-display inline-flex h-8 shrink-0 items-center rounded-full border px-3.5 text-xs font-bold transition-colors',
              !filterLevel
                ? 'bg-ink-900 border-ink-900 text-white'
                : 'text-ink-700 border-ink-200 hover:border-ink-300 bg-white',
            )}
          >
            Tous
          </button>
          {levelOptions.map((l) => (
            <button
              key={l.value}
              onClick={() => setFilterLevel(filterLevel === l.value ? '' : l.value)}
              className={cn(
                'font-display inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-xs font-bold transition-colors',
                filterLevel === l.value
                  ? 'bg-primary-500 border-primary-500 text-white'
                  : 'text-ink-700 border-ink-200 hover:border-ink-300 bg-white',
              )}
            >
              {l.label}
              <span
                className={cn(
                  'inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold',
                  filterLevel === l.value ? 'bg-white/20 text-white' : 'bg-ink-100 text-ink-500',
                )}
              >
                {l.count}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Filters row */}
      <div className="mb-5 flex items-center gap-2">
        <button
          onClick={() => setShowFilters((v) => !v)}
          className={cn(
            'font-display inline-flex h-8 items-center gap-1.5 rounded-full border px-3.5 text-xs font-bold transition-colors',
            showFilters
              ? 'bg-primary-50 text-primary-700 border-primary-200'
              : 'text-ink-700 border-ink-200 hover:border-ink-300 bg-white',
          )}
        >
          <Filter className="h-3.5 w-3.5" /> Filtrer
        </button>

        {admin && (
          <button
            onClick={() => setLiveMode((v) => !v)}
            aria-pressed={liveMode}
            className={cn(
              'font-display inline-flex h-8 items-center gap-1.5 rounded-full border px-3.5 text-xs font-bold transition-colors',
              liveMode
                ? 'bg-primary-500 border-primary-500 text-white'
                : 'text-ink-700 border-ink-200 hover:border-ink-300 bg-white',
            )}
          >
            <Radio className="h-3.5 w-3.5" /> Live School
            {liveMode && liveExercises && (
              <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-white/20 px-1 text-[10px]">
                {liveExercises.length}
              </span>
            )}
          </button>
        )}

        {hasActiveFilter && (
          <button
            onClick={() => {
              setFilterSubject('');
              setFilterDifficulty('');
              setFilterKind('');
            }}
            className="font-display text-error-600 bg-error-50 border-error-100 hover:bg-error-100 h-8 rounded-full border px-3 text-xs font-bold transition-colors"
          >
            Effacer
          </button>
        )}
      </div>

      {showFilters && (
        <div className="border-ink-200 mb-5 grid grid-cols-1 gap-3 rounded-2xl border bg-white p-4 sm:grid-cols-2">
          <div>
            <label className="font-display text-ink-600 mb-1.5 block text-xs font-semibold">
              Matière
            </label>
            <select
              value={filterSubject}
              onChange={(e) => setFilterSubject(e.target.value as SubjectSlug | '')}
              className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-10 w-full appearance-none rounded-xl border px-3 font-sans text-sm focus:outline-none"
            >
              <option value="">Toutes</option>
              {SUBJECTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-display text-ink-600 mb-1.5 block text-xs font-semibold">
              Difficulté
            </label>
            <select
              value={filterDifficulty}
              onChange={(e) =>
                setFilterDifficulty(e.target.value as 'easy' | 'medium' | 'hard' | '')
              }
              className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-10 w-full appearance-none rounded-xl border px-3 font-sans text-sm focus:outline-none"
            >
              <option value="">Toutes</option>
              <option value="easy">Facile</option>
              <option value="medium">Moyen</option>
              <option value="hard">Difficile</option>
            </select>
          </div>
          <div>
            <label className="font-display text-ink-600 mb-1.5 block text-xs font-semibold">
              Type
            </label>
            <select
              value={filterKind}
              onChange={(e) =>
                setFilterKind(e.target.value as '' | 'classic' | 'fundamental' | 'diagnostic')
              }
              className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-10 w-full appearance-none rounded-xl border px-3 font-sans text-sm focus:outline-none"
            >
              <option value="">Tous</option>
              <option value="classic">Exercices classiques</option>
              <option value="fundamental">Exercices fondamentaux</option>
              <option value="diagnostic">Exercices diagnostic</option>
            </select>
          </div>
        </div>
      )}

      {liveMode ? (
        <LiveSchoolView isLoading={liveLoading} upcoming={live.upcoming} past={live.past} />
      ) : searching ? (
        <SearchResultsView isLoading={searchLoading} results={searchDisplayed} query={q} />
      ) : isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-ink-100 h-28 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : grouped.length === 0 ? (
        exercises?.length === 0 ? (
          <EmptyExercises />
        ) : (
          <p className="text-ink-400 py-10 text-center font-sans text-sm">
            Aucun exercice ne correspond aux filtres sélectionnés.
          </p>
        )
      ) : (
        <div className="space-y-8">
          {grouped.map(([subjectKey, sg]) => (
            <div key={subjectKey}>
              {/* Subject header */}
              <div className="mb-4 flex items-center gap-3">
                <h2 className="font-display text-ink-500 shrink-0 text-[11px] font-bold uppercase tracking-widest">
                  {sg.label}
                </h2>
                <div className="border-ink-200 flex-1 border-t" />
              </div>

              <div className="space-y-6">
                {sg.chapters.map((cg) => (
                  <div key={cg.id ?? cg.title}>
                    {/* Chapter header */}
                    <div className="mb-3 flex items-baseline gap-2">
                      <span className="font-display text-ink-800 text-sm font-semibold">
                        {cg.title}
                      </span>
                      {cg.level && (
                        <span className="text-ink-400 font-sans text-xs">
                          {LEVEL_LABEL[cg.level]}
                        </span>
                      )}
                      <span className="text-ink-400 font-sans text-xs">
                        · {chapterTotals.get(cg.id ?? cg.title) ?? cg.exercises.length} exercice
                        {(chapterTotals.get(cg.id ?? cg.title) ?? cg.exercises.length) > 1
                          ? 's'
                          : ''}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {cg.exercises.map((ex) => (
                        <ExerciseCard key={ex.id} exercise={ex} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <Paginator
            page={paged.page}
            pageCount={paged.pageCount}
            onPageChange={changePage}
            summary={`${paged.from}–${paged.to} sur ${paged.total} exercices`}
            label="Pages des exercices"
          />
        </div>
      )}
    </div>
  );
}

/** Pur — regroupe par matière puis chapitre, dans l'ordre d'apparition. */
export function groupExercises(list: MyExerciseSummary[]): [string, SubjectGroup][] {
  const map = new Map<string, SubjectGroup>();
  for (const ex of list) {
    const subjectKey = ex.subject ?? '(autre)';
    if (!map.has(subjectKey)) {
      map.set(subjectKey, {
        label: SUBJECT_LABEL[subjectKey as SubjectSlug] ?? subjectKey,
        chapters: [],
      });
    }
    const sg = map.get(subjectKey)!;
    const chapterKey = ex.chapter_id ?? ex.chapter_title;
    let cg = sg.chapters.find((c) => (c.id ?? c.title) === chapterKey);
    if (!cg) {
      cg = { id: ex.chapter_id, title: ex.chapter_title, level: ex.level, exercises: [] };
      sg.chapters.push(cg);
    }
    cg.exercises.push(ex);
  }
  return Array.from(map.entries());
}

function LiveSchoolView({
  isLoading,
  upcoming,
  past,
}: {
  isLoading: boolean;
  upcoming: LiveSchoolExercise[];
  past: LiveSchoolExercise[];
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-ink-100 h-44 animate-pulse rounded-2xl" />
        ))}
      </div>
    );
  }

  if (upcoming.length === 0 && past.length === 0) {
    return (
      <div className="border-ink-200 rounded-2xl border bg-white p-10 text-center">
        <div className="bg-primary-50 mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl">
          <Radio className="text-primary-500 h-7 w-7" />
        </div>
        <h3 className="font-display text-ink-900 mb-1 text-lg font-extrabold">Aucun défi Live</h3>
        <p className="text-ink-500 mx-auto max-w-xs font-sans text-sm">
          Aucun exercice n'est rattaché à un défi Live School (ou aucun ne correspond aux filtres).
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {upcoming.length > 0 && <LiveSection title="À venir" items={upcoming} />}
      {past.length > 0 && <LiveSection title="Déjà diffusés" items={past} />}
    </div>
  );
}

function LiveSection({ title, items }: { title: string; items: LiveSchoolExercise[] }) {
  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="font-display text-ink-500 shrink-0 text-[11px] font-bold uppercase tracking-widest">
          {title} · {items.length} défi{items.length > 1 ? 's' : ''}
        </h2>
        <div className="border-ink-200 flex-1 border-t" />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {items.map((item) => (
          <ExerciseCard key={item.live.challenge_id} exercise={item} live={item.live} />
        ))}
      </div>
    </div>
  );
}

function SearchResultsView({
  isLoading,
  results,
  query,
}: {
  isLoading: boolean;
  results: MyExerciseSummary[];
  query: string;
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
        ))}
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <p className="text-ink-400 py-10 text-center font-sans text-sm">
        Aucun exercice ne correspond à «&nbsp;{query}&nbsp;».
      </p>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="font-display text-ink-500 shrink-0 text-[11px] font-bold uppercase tracking-widest">
          {results.length} résultat{results.length > 1 ? 's' : ''} · tous auteurs
        </h2>
        <div className="border-ink-200 flex-1 border-t" />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {results.map((ex) => (
          <ExerciseCard key={ex.id} exercise={ex} />
        ))}
      </div>
    </div>
  );
}

function ExerciseCard({
  exercise: ex,
  live,
}: {
  exercise: MyExerciseSummary;
  live?: LiveSchoolExercise['live'];
}) {
  const diff = DIFFICULTY_META[ex.difficulty] ?? DIFFICULTY_META.medium;
  const stat = STATUS_META[ex.status] ?? STATUS_META.draft;
  const liveStat = live ? LIVE_STATUS_META[live.status] : null;

  return (
    <Link
      to="/tutor/exercises/$exerciseId"
      params={{ exerciseId: ex.id }}
      className="border-ink-200 hover:border-primary-200 block rounded-2xl border bg-white p-5 transition-all hover:shadow-md active:scale-[.99]"
    >
      {/* Diffusion Live School — date + heure + statut */}
      {live && liveStat && (
        <div className="bg-primary-50 mb-2.5 flex items-center gap-2 rounded-lg px-2.5 py-1.5">
          <Radio className="text-primary-600 h-3.5 w-3.5 shrink-0" />
          <span className="font-display text-primary-700 text-xs font-bold capitalize">
            {liveDateFmt.format(new Date(live.starts_at))}
          </span>
          <span
            className={cn(
              'font-display ml-auto inline-flex h-5 shrink-0 items-center rounded-full px-2 text-[10px] font-bold uppercase tracking-wide',
              liveStat.cls,
            )}
          >
            {liveStat.label}
          </span>
        </div>
      )}

      {/* Top badges */}
      <div className="mb-2.5 flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'font-display inline-flex h-5 rounded-full border px-2 text-[10px] font-bold uppercase tracking-wide',
            diff.cls,
          )}
        >
          {diff.label}
        </span>
        <span
          className={cn(
            'font-display inline-flex h-5 rounded-full px-2 text-[10px] font-bold uppercase tracking-wide',
            stat.cls,
          )}
        >
          {stat.label}
        </span>
        {ex.is_fundamental && (
          <>
            <span className="font-display bg-primary-50 text-primary-700 inline-flex h-5 rounded-full px-2 text-[10px] font-bold uppercase tracking-wide">
              Fondamental
            </span>
            <span className="font-display bg-xp-50 text-xp-600 inline-flex h-5 rounded-full px-2 text-[10px] font-bold uppercase tracking-wide">
              Gratuit
            </span>
            <span className="font-display bg-accent-50 text-accent-600 inline-flex h-5 rounded-full px-2 text-[10px] font-bold uppercase tracking-wide">
              Source Warmup
            </span>
          </>
        )}
        {ex.is_diagnostic && (
          <>
            <span className="font-display bg-primary-50 text-primary-700 inline-flex h-5 rounded-full px-2 text-[10px] font-bold uppercase tracking-wide">
              Diagnostic
            </span>
            <span className="font-display bg-xp-50 text-xp-600 inline-flex h-5 rounded-full px-2 text-[10px] font-bold uppercase tracking-wide">
              Gratuit
            </span>
          </>
        )}
      </div>

      {/* Title + vignette */}
      <div className="flex items-start gap-3">
        {ex.illustration_url && (
          <img
            src={ex.illustration_url}
            alt=""
            loading="lazy"
            className="border-ink-100 h-14 w-14 shrink-0 rounded-xl border object-cover"
          />
        )}
        <h3 className="font-display text-ink-900 line-clamp-2 flex-1 text-[15px] font-extrabold leading-tight">
          <MathText>{ex.title}</MathText>
        </h3>
      </div>

      {/* Auteur */}
      {ex.author?.display_name && (
        <div className="mt-2.5 flex min-w-0 items-center gap-1.5">
          <AvatarCircle size="xs" name={ex.author.display_name} src={ex.author.avatar_url} />
          <span className="text-ink-500 truncate font-sans text-xs">{ex.author.display_name}</span>
        </div>
      )}

      {/* Footer */}
      <div className="border-ink-100 mt-3 flex items-center justify-between border-t pt-3">
        <span className="text-ink-500 font-sans text-xs">
          {ex.subject ? (SUBJECT_LABEL[ex.subject] ?? ex.subject) : ''}
          {ex.level ? ` · ${LEVEL_LABEL[ex.level] ?? ex.level}` : ''}
        </span>
        <div className="text-ink-500 flex items-center gap-2 text-xs">
          <span className="font-mono">
            {ex.step_count} étape{ex.step_count !== 1 ? 's' : ''}
          </span>
          {ex.estimated_minutes && <span>{ex.estimated_minutes} min</span>}
        </div>
      </div>
    </Link>
  );
}

function EmptyExercises() {
  return (
    <div className="border-ink-200 rounded-2xl border bg-white p-10 text-center">
      <div className="bg-accent-50 mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl">
        <BookMarked className="text-accent-400 h-7 w-7" />
      </div>
      <h3 className="font-display text-ink-900 mb-1 text-lg font-extrabold">Bibliothèque vide</h3>
      <p className="text-ink-500 mx-auto mb-6 max-w-xs font-sans text-sm">
        Crée ton premier exercice avec l'assistant IA.
      </p>
      <Link
        to="/tutor/exercises/new"
        className="bg-accent-400 font-display hover:bg-accent-500 inline-flex h-10 items-center gap-2 rounded-xl px-5 text-sm font-bold text-white shadow-md transition-all"
      >
        <Plus className="h-4 w-4" />
        Créer un exercice
      </Link>
    </div>
  );
}
